import { useCallback, useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';

import { ActionSubjectIdKey } from '@/analytics/constants';
import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftOrganizationSummary } from '@/state/actions';
import type { OrganizationSummary as OrganizationSummaryState } from '@/state/types';

import { useOrganizationDetailsSectionAnalytics } from '../hooks/use-organization-details-section-analytics';

import { messages } from './messages';
import { OrganizationSummaryCard } from './organization-summary-card';

import type { FastshiftOrganizationSummaryResponseData } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

const styles = cssMap({
	summaryLayout: {
		'@media not all and (min-width: 48rem)': {
			flexDirection: 'column',
		},
	},
	summaryMetric: {
		display: 'flex',
		flexBasis: 0,
		flexGrow: 1,
		minWidth: 0,
	},
});

type SummaryMetric = {
	heading: MessageDescriptor;
	key: Exclude<keyof FastshiftOrganizationSummaryResponseData, 'orgId' | 'orgName'>;
};

const summaryMetrics = [
	{ heading: messages.dataCenterCardHeading, key: 'dataCentersAssessed' },
	{ heading: messages.areaImprovementCardHeading, key: 'areasForImprovement' },
	{ heading: messages.migrationCompletedCardHeading, key: 'migrationsCompleted' },
	{ heading: messages.migrationInProgressCardHeading, key: 'migrationsInProgress' },
	{ heading: messages.unsuccessfulMigrationCardHeading, key: 'migrationsUnsuccessful' },
] as const satisfies ReadonlyArray<SummaryMetric>;

const notLoadedSummary: OrganizationSummaryState = {
	pending: false,
	error: null,
	data: null,
};

export type OrganizationSummaryProps = {
	domainId: string;
	orgId: string;
};

export const OrganizationSummary = ({ domainId, orgId }: OrganizationSummaryProps) => {
	const { formatMessage } = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const {
		data: organizationSummary,
		pending,
		error,
	} = useFastshiftSelector((state) =>
		state.organizationDetail.domainId === domainId && state.organizationDetail.orgId === orgId
			? state.organizationDetail.summary
			: notLoadedSummary,
	);

	const fetchOrganizationSummary = useCallback(() => {
		dispatch(
			getFastshiftOrganizationSummary({
				stargateClient,
				domainId,
				orgId,
			}),
		);
	}, [dispatch, stargateClient, domainId, orgId]);

	useEffect(() => {
		fetchOrganizationSummary();
	}, [fetchOrganizationSummary]);

	const isLoading = pending || (organizationSummary === null && !error);
	useOrganizationDetailsSectionAnalytics({
		organizationId: orgId,
		actionSubjectId: ActionSubjectIdKey.ORGANIZATION_SUMMARY_SECTION,
		status: isLoading ? null : error ? 'error' : 'normal',
	});

	return (
		<Stack space="space.100">
			<Heading size="small" as="h2">
				{formatMessage(messages.organizationSummaryTitle)}
			</Heading>
			{error ? (
				<GenericErrorDisplay
					customMessage={formatMessage(messages.errorDescription)}
					onRefresh={fetchOrganizationSummary}
				/>
			) : (
				<Flex columnGap="space.200" rowGap="space.100" xcss={styles.summaryLayout}>
					{summaryMetrics.map(({ heading, key }) => (
						<Box key={key} xcss={styles.summaryMetric}>
							<OrganizationSummaryCard
								title={formatMessage(heading)}
								value={organizationSummary?.[key] ?? '-'}
								isLoading={isLoading}
							/>
						</Box>
					))}
				</Flex>
			)}
		</Stack>
	);
};
